// API Client
import { AxiosResponse, isAxiosError } from "axios";
import CarbonClient from "../CarbonClient";

// Types
import { UpdateProjectBaseInfoType, UpdateProjectCalculationAssumptionsType, UpdateModelsType } from "@/features/BuildingProject";
import { BuildingProjectType } from "@/features/BuildingProject";
import {
  DashboardEmissionBudgetType,
  DashboardMappingType,
  DashboardSiteBudgetType,
  DashboardTransportInstallationBudgetType,
  DashboardUnitType,
} from "@/features/Dashboard";

/**
 * `GET api/BuildingProject/{id}` is the single most expensive call in the app (measured
 * 2.0-3.7s per request). On a cold load of any project page it used to be requested TWICE,
 * CONCURRENTLY, because two different stores read the same project through the two functions
 * below — `getBuildingProjectById` (the ProjectWrapper provider) and `getBuildingProject` (the
 * project store) — both fired from the same `Promise.all` in `ProjectDetailLayout.vue`'s
 * `loadProject`. Two identical requests hitting the API at once contend with each other rather
 * than running in parallel (measured: one finished +1993ms, the other +3708ms — the second
 * effectively paid for the first), so the duplicate was strictly worse than not deduplicating.
 *
 * This map joins concurrent callers onto ONE underlying HTTP request per project id, keyed by
 * the stringified id. It is deliberately request DE-DUPLICATION, NOT A CACHE: the entry is
 * removed the moment the request settles, success or failure, so a SEQUENTIAL second call always
 * issues a fresh request — there is no TTL and no result is ever stored past the request that
 * produced it. That distinction is the entire safety argument: a forced refresh after a save
 * (e.g. `forceUpdateProjectWrapperAndAdditionalInfo`) must always see the freshly saved data,
 * never a result served from an earlier, now-stale, in-flight join.
 *
 * Deliberately typed as the untyped `AxiosResponse` (not `AxiosResponse<BuildingProjectType>`):
 * `getBuildingProjectById` has always read `response.data` untyped, and its callers rely on that
 * looseness (e.g. assigning it straight onto a wrapper). Giving the shared response a concrete
 * type here would tighten `getBuildingProjectById`'s inferred return type too and could break its
 * callers. `getBuildingProject` casts to its own, already-typed contract below instead.
 */
const inFlightBuildingProjectRequests = new Map<string, Promise<AxiosResponse>>();

const getBuildingProjectResponse = (buildingProjectId: string): Promise<AxiosResponse> => {
  const existingRequest = inFlightBuildingProjectRequests.get(buildingProjectId);
  if (existingRequest) return existingRequest;

  const request = CarbonClient.get(`api/BuildingProject/${buildingProjectId}`).finally(() => {
    inFlightBuildingProjectRequests.delete(buildingProjectId);
  });

  inFlightBuildingProjectRequests.set(buildingProjectId, request);
  return request;
};

export const getBuildingProjectById = async (buildingProjectId: string | string[]) => {
  try {
    // String(...) mirrors what the template literal below used to do to an array id
    // (Array.prototype.toString → comma-joined) — the join key and the URL segment stay identical
    // to today's single request.
    const response = await getBuildingProjectResponse(String(buildingProjectId));
    if (response.status === 200) {
      return response.data;
    } else {
      return { status: response.status };
    }
  } catch (error: any) {
    console.error("Error fetching building project:", error);
    return { status: error.response?.status || 500 };
  }
};

export const getBuildingProject = async (projectId: string): Promise<BuildingProjectType> => {
  try {
    const response = await getBuildingProjectResponse(projectId);

    if (response.status === 200) {
      // See the comment on `inFlightBuildingProjectRequests` above: the shared response is
      // deliberately untyped so it doesn't affect `getBuildingProjectById`'s inference, so this
      // function restates its own, pre-existing contract here instead.
      return response.data as BuildingProjectType;
    } else {
      throw new Error(`Failed to fetch project: ${response.status}`);
    }
  } catch (error: any) {
    console.error("Error fetching building project:", error);
    throw error;
  }
};

export const getBuildingProjectUserList = async (buildingProjectId: string) => {
  const response = await CarbonClient.get(`api/BuildingProject/buildingprojectuserlist/${buildingProjectId}`);
  if (response.status === 200) {
    return response.data;
  } else {
    return { status: response.status };
  }
};

export const getBuildingProjectEnums = async () => {
  const response = await CarbonClient.get(`/api/Enums/buildingprojectenums`);
  if (response.status === 200) {
    return response.data;
  } else {
    return { status: response.status };
  }
};

export const getProjectNameExists = async (projectName: string) => {
  const response = await CarbonClient.get(`api/BuildingProject/nameexists`, {
    params: { buildingProjectName: projectName },
  });
  if (response.status === 200) {
    return response.data;
  } else {
    return { status: response.status };
  }
};

//PUT Requests
export const updateProjectBaseInfo = async (buildingProjectId: string, projectBaseInfo: UpdateProjectBaseInfoType) => {
  const response = await CarbonClient.put(`/api/BuildingProject/updatebaseinfo/${buildingProjectId}`, projectBaseInfo);
  if (response.status === 200) {
    return response.status;
  } else {
    return { status: response.status };
  }
};
export const updateProjectCalculationAssumptions = async (
  buildingProjectId: string,
  projectCalculationAssumptions: UpdateProjectCalculationAssumptionsType
) => {
  const response = await CarbonClient.put(
    `/api/BuildingProject/updatecalculationassumptions/${buildingProjectId}`,
    projectCalculationAssumptions
  );
  if (response.status === 200) {
    return response.status;
  } else {
    return { status: response.status };
  }
};

export const updateProjectActiveBranches = async (buildingProjectId: string, branches: UpdateModelsType) => {
  const response = await CarbonClient.put(`/api/BuildingProject/UpdateBranch`, branches, {
    params: { buildingProjectId },
  });
  if (response.status === 200) {
    return response.status;
  } else {
    return { status: response.status };
  }
};

/**
 * One model-sync pass, now (RTLCA-5667): every project model gets its Speckle model (created when
 * absent, matched by name), the Speckle-owned columns are refreshed, and Speckle-only models holding
 * versions are imported as inactive rows. The background sweeper converges the same way on its own
 * within about 30 seconds; this is the nudge right after a model was added or removed, so the row
 * exists on Speckle before anything else reads the stream. A project without an integration is a
 * quiet no-op. 204 when the pass ran; 400 with a plain-text reason when the caller has no linked
 * Speckle account (axios throws it — read it with `speckleSyncFailureMessage`).
 */
export const syncProjectIntegrations = async (buildingProjectId: string): Promise<void> => {
  await CarbonClient.post(`/api/BuildingProject/${buildingProjectId}/integrations/sync`, "");
};

export const speckleSyncFailureMessage = (error: unknown): string => {
  if (isAxiosError(error) && error.response?.status === 400 && typeof error.response.data === "string") {
    return error.response.data;
  }
  return "Speckle could not be synced right now.";
};

export const inviteUserToBuildingProject = async (
  req: {
    inviteeUserId: string;
    inviteeEmail: string;
    roleId: string;
  },
  buildingProjectId: string
) => {
  const response = await CarbonClient.put(`/api/BuildingProject/inviteusertobuildingproject/${buildingProjectId}`, req);
  if (response.status === 200) {
    return response.data;
  } else {
    return { status: response.status };
  }
};

export const toggleFavorite = async (buildingProjectId: string | string[], isFavorite: boolean) => {
  const response = await CarbonClient.put(
    `/api/BuildingProject/buildingproject/${buildingProjectId}/favorite?favorite=${isFavorite}`
  );
  if (response.status === 200) {
    return response.data;
  } else {
    return { status: response.status };
  }
};

//POST Requests

export type EmissionBudgetItem = {
  budget?: DashboardEmissionBudgetType;
  // BR18 2025 only — A4-A5 lives outside the material budget there, with its own threshold.
  // Null for every other calculation type, where A4/A5 remain inside `budget`.
  transportInstallationBudget?: DashboardTransportInstallationBudgetType | null;
  // Finland only — Talo 2000 "1.1" is reported apart from the building and is already out of `budget`.
  // Null for every other project, which splits nothing.
  siteBudget?: DashboardSiteBudgetType | null;
  mapping: DashboardMappingType;
  unit: DashboardUnitType;
};

export type EmissionBudgetResponse = {
  [key: string]: EmissionBudgetItem;
};

export const getEmissionBudget = async (projectIds: string[]): Promise<EmissionBudgetResponse | undefined> => {
  const response = await CarbonClient.post("api/BuildingProject/emission-budget", { projectIds });
  if (response.status === 200) {
    return response.data;
  }

  return undefined;
};

/**
 * Thrown when the API predates this endpoint. Rollout order is Hasura → API → UI, so the UI has to
 * cope with an API that has not caught up yet — see the composable, which hides the column rather
 * than painting an error on every row.
 */
export class EmissionBudgetEndpointMissingError extends Error {}

/**
 * One project's emission budget as of each of several revisions, keyed by REVISION id.
 *
 * Deliberately not `getEmissionBudget` with an extra argument: that response is keyed by project
 * id, so several revisions of one project would collide on a single key.
 */
export const getRevisionEmissionBudgets = async (
  projectId: string,
  revisionIds: string[]
): Promise<EmissionBudgetResponse | undefined> => {
  try {
    const response = await CarbonClient.post(`api/BuildingProject/${projectId}/emission-budget/by-revision`, {
      revisionIds,
    });

    return response.status === 200 ? response.data : undefined;
  } catch (error) {
    // CarbonClient rejects on any non-2xx, so a missing endpoint arrives here rather than as a status.
    if (isAxiosError(error) && error.response?.status === 404) throw new EmissionBudgetEndpointMissingError();
    throw error;
  }
};

export type CreateBaseProjectPayload = {
  name: string;
  buildingTypeId: number;
  yearOfConstruction: number;
  yearOfCommissioning: number;
  // RTLCA-5661: optional, so a caller with nothing to say about either leaves them off rather than
  // sending a value that means something (0, an empty array).
  projectStatusId?: number;
  models?: Array<{ name: string; active: boolean }>;
};

export type CreateBaseProjectResult = {
  id: string;
  activeRevisionId: string;
};

/**
 * The minimal project create (RTLCA-5404): the project row and the creator's project.admin
 * membership in one Hasura transaction, returning both ids — the revision comes from the
 * project_ensure_active_revision trigger, not from the API.
 *
 * The four required columns are the ones a client cannot write itself: `project` has no user-role
 * insert permission, and this endpoint runs over the admin secret. Everything else the wizard
 * collects is written straight to Hasura afterwards — except, since RTLCA-5661, the project's
 * status and its initial named models, which this endpoint also accepts because they are seeded at
 * creation rather than left to a later screen.
 *
 * Deliberately absent server-side: Speckle, notifications, and any location or calculation row.
 * No Marten stream either — BuildingProjectQueryRepository materializes one on first read.
 */
export const createBaseProject = async (payload: CreateBaseProjectPayload): Promise<CreateBaseProjectResult> => {
  const response = await CarbonClient.post("api/BuildingProject/base", payload);
  return response.data;
};

/**
 * Attach a Speckle stream to a project that already exists. Requires EditProject on that project.
 *
 * This is what makes a 3D model possible on a Hasura-created project. The stream id used to travel in
 * the `POST api/BuildingProject` payload, which this flow no longer sends, and it cannot go through
 * Hasura either — `project.speckle_stream_id` is readable but carries no insert or update permission,
 * and the base endpoint's payload is four columns that do not include it.
 *
 * The API writes the stream to the Marten aggregate (what the connector's stream-to-project lookup
 * reads) and, since RTLCA-5546, to Hasura as well: the stream id. The project_model rows are the
 * project's own and exist before the attach (RTLCA-5629); the attach enqueues a sync that gives each a
 * Speckle model by name, matched case-insensitively. `activeModels` still says which are active. Names
 * are lower-cased, as Speckle stores them. An empty list is not the same as no list: with no body every
 * branch is active.
 */
export const updateProjectSpeckleStream = async (
  buildingProjectId: string,
  speckleStreamId: string,
  activeModels?: Array<string>
): Promise<void> => {
  await CarbonClient.put(
    `api/BuildingProject/${buildingProjectId}/updateSpeckleMetaData/${speckleStreamId}`,
    activeModels === undefined ? undefined : { activeModels }
  );
};

/**
 * What a role change answers. `speckleWarning` is set when the role was saved but the member's access
 * to the project's Speckle model could not be brought in line with it (RTLCA-5531), so the caller can
 * say so rather than report a clean success.
 */
export type UpdateUserRoleResult = { speckleWarning?: string | null };

/**
 * Changes a member's project role. A 409 (thrown, as axios does for any non-2xx) means the change was
 * refused before anything was written — the member is the only owner of the project's Speckle model —
 * and `response.data.message` says why.
 */
export const updateUserRole = async (
  buildingProjectId: string,
  userId: string,
  roleId: string
): Promise<UpdateUserRoleResult> => {
  const response = await CarbonClient.post(`api/BuildingProject/updateuserbuildingprojectrole/${buildingProjectId}`, {
    userId,
    roleId,
  });
  // Every response that reaches here is a 2xx: the endpoint answers 200 with the result body.
  return response.data ?? {};
};

//DELETE
export const deleteBuildingProject = async (buildingProjectId: string | string[]) => {
  const response = await CarbonClient.delete(`api/BuildingProject`, {
    params: { buildingProjectId },
  });
  if (response.status === 201 || response.status === 200) {
    return response.status;
  }
};
export const uninviteUserFromBuildingProject = async (buildingProjectId: string, userId: string) => {
  const response = await CarbonClient.delete(`/api/BuildingProject/${buildingProjectId}/uninvite`, {
    params: { userId },
  });
  if (response.status === 201 || response.status === 200) {
    return response.status;
  }
};
